import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '@/store/cart';
import { useAuth } from '@/contexts/AuthContext';
import { CartItem } from '@/components/cart/CartItem';
import { CartStoreInfo } from '@/components/cart/CartStoreInfo';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';
import { cartService } from '@/services/cart';
import { restaurantService, type RestaurantWithProducts } from '@/services/restaurant';

export function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  const {
    storeId,
    storeName,
    orderType,
    items,
    usedPoints,
    desiredPickupTime,
    setUsedPoints,
    getTotalPrice,
    getFinalPrice,
    canAddMoreItems,
  } = useCartStore();
  const isDeadlineOrder = orderType === 'DEADLINE_DISCOUNT';

  const [userPoints, setUserPoints] = useState(0);
  const [pointInput, setPointInput] = useState('');
  const [isLoadingPoints, setIsLoadingPoints] = useState(true);
  const [restaurantInfo, setRestaurantInfo] = useState<RestaurantWithProducts | null>(null);
  const [isLoadingRestaurant, setIsLoadingRestaurant] = useState(true);
  const [isValidatingCart, setIsValidatingCart] = useState(false);

  // Validate cart stock on page load
  useEffect(() => {
    const validateCartStock = async () => {
      if (!user?.id || !storeId || items.length === 0) return;
      
      setIsValidatingCart(true);
      try {
        const cart = await cartService.getCurrentCart(user.id);
        if (cart) {
          const validation = await cartService.validateCartStock(cart.id);
          
          if (!validation.success && validation.unavailableItems.length > 0) {
            showToast(`다음 상품의 재고가 부족하여 장바구니에서 제거되었습니다: ${validation.unavailableItems.join(', ')}`, 4000);
            
            // 재고 부족 상품들을 장바구니에서 제거
            // TODO: 실제 제거 로직 구현 필요
          }
        }
      } catch (error) {
        console.error('Cart validation error:', error);
      } finally {
        setIsValidatingCart(false);
      }
    };

    validateCartStock();
  }, [user?.id, storeId, items.length, showToast]);

  // Fetch user points
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!user?.id) {
        setIsLoadingPoints(false);
        return;
      }

      try {
        // 이제 user_points.user_id가 직접 users.id를 참조하므로 간단하게 조회
        const { data: pointsData, error: pointsError } = await supabase
          .from('user_points')
          .select('current_balance')
          .eq('user_id', user.id)
          .single();

        if (pointsError) {
          console.error('Failed to fetch points:', pointsError);
          setUserPoints(0);
        } else {
          setUserPoints(pointsData?.current_balance || 0);
        }
      } catch (error) {
        console.error('Error fetching points:', error);
        setUserPoints(0);
      } finally {
        setIsLoadingPoints(false);
      }
    };

    fetchUserPoints();
  }, [user]);

  // Fetch restaurant info for time slot calculation
  useEffect(() => {
    const fetchRestaurantInfo = async () => {
      if (!storeId) {
        setIsLoadingRestaurant(false);
        return;
      }

      try {
        const includeProducts = orderType === 'DEADLINE_DISCOUNT';
        const result = await restaurantService.getRestaurantById(storeId, includeProducts);
        if (result.success && result.data) {
          setRestaurantInfo(result.data);
        } else {
          console.error('Failed to fetch restaurant:', result.error);
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
      } finally {
        setIsLoadingRestaurant(false);
      }
    };

    fetchRestaurantInfo();
  }, [storeId]);

  // DB와 로컬 장바구니 동기화 검증
  useEffect(() => {
    const validateCartWithDB = async () => {
      if (!user?.id || !storeId || items.length === 0) return;

      try {
        const dbCart = await cartService.getCurrentCart(user.id);
        
        if (!dbCart || !dbCart.items || dbCart.items.length === 0) {
          // DB에 장바구니가 없으면 로컬 상태를 DB에 동기화
          return;
        }
        
        // DB와 로컬 상태 비교 (간단한 검증)
        if (dbCart.items.length !== items.length) {
          showToast('장바구니 상태를 확인하는 중입니다...');
        }
        
        // 여기서 필요시 동기화 로직 추가 가능
      } catch (error) {
        console.error('Error validating cart with DB:', error);
      }
    };

    validateCartWithDB();
  }, [user?.id, storeId, items.length, showToast]);

  // Initialize point input
  useEffect(() => {
    setPointInput(usedPoints.toString());
  }, [usedPoints]);

  useEffect(() => {
    if (!isDeadlineOrder) return;

    if (usedPoints !== 0) {
      setUsedPoints(0);
    }

    if (pointInput !== '') {
      setPointInput('');
    }
  }, [isDeadlineOrder, usedPoints, pointInput, setUsedPoints]);

  const handlePointInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isDeadlineOrder) return;

    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    const points = parseInt(value) || 0;
    const maxPoints = Math.min(userPoints, getTotalPrice());
    
    if (points <= maxPoints) {
      setPointInput(value);
      setUsedPoints(points);
    } else {
      setPointInput(maxPoints.toString());
      setUsedPoints(maxPoints);
      showToast(`최대 ${maxPoints.toLocaleString()}포인트까지 사용 가능합니다`);
    }
  };

  const handleAddMore = () => {
    if (!canAddMoreItems()) {
      showToast('장바구니에는 최대 5개의 다른 상품만 담을 수 있습니다');
      return;
    }
    navigate(`/store/${storeId}`);
  };

  const handleCheckout = async () => {
    if (items.length === 0) {
      showToast('장바구니가 비어있습니다');
      return;
    }

    // Real-time stock validation before checkout
    if (user?.id) {
      try {
        setIsValidatingCart(true);
        const cart = await cartService.getCurrentCart(user.id);
        
        if (cart) {
          const validation = await cartService.validateCartStock(cart.id);
          
          if (!validation.success) {
            showToast(validation.error || '일부 상품의 재고가 부족합니다', 3000);
            return;
          }
        }
      } catch (error) {
        console.error('Checkout validation error:', error);
        showToast('재고 확인 중 오류가 발생했습니다', 3000);
        return;
      } finally {
        setIsValidatingCart(false);
      }
    }

    // Navigate to checkout page
    navigate('/checkout', {
      state: {
        storeId,
        storeName,
        orderType,
        items,
        usedPoints,
        desiredPickupTime,
        totalPrice: getTotalPrice(),
        finalPrice: getFinalPrice(userPoints),
      }
    });
  };

  const totalPrice = getTotalPrice();
  const finalPrice = getFinalPrice(userPoints);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    return null; // Will redirect to login
  }

  if (!storeId || items.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
          <div className="flex items-center justify-center px-4 py-3 relative">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img
                src="/icons/icon_back.svg"
                alt="뒤로가기"
                width="24"
                height="24"
              />
            </button>
            <h1 className="text-2xl font-bold">장바구니</h1>
          </div>
        </div>

        {/* Empty Cart */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-4 text-gray-300">
              <svg width="96" height="96" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M3 3H5L6.5 9M6.5 9H17.5L21 3M6.5 9L8 16H16L17.5 9M6.5 9H17.5M8 16C6.89543 16 6 16.8954 6 18C6 19.1046 6.89543 20 8 20C9.10457 20 10 19.1046 10 18C10 16.8954 9.10457 16 8 16ZM16 16C14.8954 16 14 16.8954 14 18C14 19.1046 14.8954 20 16 20C17.1046 20 18 19.1046 18 18C18 16.8954 17.1046 16 16 16Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-6">장바구니가 비어있습니다</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
            >
              상품 보러가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex items-center justify-center px-4 py-3 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              width="24"
              height="24"
            />
          </button>
          <h1 className="text-2xl font-bold">장바구니</h1>
        </div>
      </div>

      {/* Cart Content */}
      <div className="flex-1 pb-32">
        {/* Store Info */}
        <div className="px-4 py-4">
          <CartStoreInfo 
            storeId={storeId} 
            restaurantInfo={restaurantInfo}
            isLoading={isLoadingRestaurant}
          />
        </div>

        {/* Cart Items */}
        <div className="px-4 py-4 space-y-3">
          {items.map((item) => (
            <CartItem
              key={item.productId}
              item={item}
            />
          ))}
          
          {/* Add More Button */}
          {canAddMoreItems() && (
            <button
              onClick={handleAddMore}
              className="w-full py-3 bg-[#F5F5F5] rounded-lg flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="#222222" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span className="text-lg text-[#222222]">주문 추가</span>
            </button>
          )}
        </div>

        {/* Point Usage */}
        {user && !isLoadingPoints && (
          <div className="px-4 py-6 border-t-2 border-gray-100">
            <h3 className="text-lg font-semibold mb-4">포인트 사용</h3>
            
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">보유 포인트</span>
              <span className="font-medium">{userPoints.toLocaleString()}P</span>
            </div>
            
            <div className="flex justify-end mb-3">
              <button
                onClick={() => {
                  const maxPoints = Math.min(userPoints, getTotalPrice());
                  setPointInput(maxPoints.toString());
                  setUsedPoints(maxPoints);
                }}
                disabled={isDeadlineOrder}
                aria-disabled={isDeadlineOrder}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                전액사용
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={pointInput}
                onChange={handlePointInputChange}
                placeholder="0"
                disabled={isDeadlineOrder}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              />
              <span className="text-gray-600">P</span>
            </div>
            {isDeadlineOrder && (
              <p className="mt-2 text-xs text-gray-500">마감할인 상품은 포인트 적립만 가능해요.</p>
            )}
          </div>
        )}

        {/* Price Summary */}
        <div className="px-4 py-4 border-t-2 border-gray-100">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">상품 금액</span>
              <span className="font-medium">{totalPrice.toLocaleString()}원</span>
            </div>
            {usedPoints > 0 && (
              <div className="flex items-center justify-between text-green-600">
                <span>포인트 할인</span>
                <span>-{usedPoints.toLocaleString()}원</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-lg font-semibold">결제 예정 금액</span>
              <span className="text-xl font-bold text-green-600">
                {finalPrice.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4" style={{ zIndex: 60 }}>
        <button
          onClick={handleCheckout}
          disabled={isValidatingCart}
          className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-colors ${
            isValidatingCart 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
          }`}
        >
          {isValidatingCart ? (
            <>
              <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
              <span>재고 확인 중...</span>
            </>
          ) : (
            <>
              <span>합계 {finalPrice.toLocaleString()}원</span>
              <span>결제하기</span>
            </>
          )}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
}
